/**
 * Utility functions for managing exclusive job requests workflow
 */

/** Default exclusive period in hours (Starter / organic) */
const DEFAULT_EXCLUSIVE_HOURS = 24;

/** Growth-tier exclusive period in hours (organic + paid) */
const GROWTH_EXCLUSIVE_HOURS = 48;

/**
 * Returns the exclusive period duration in hours based on subscription tier.
 * - Growth tier: 48 hours (organic + paid leads)
 * - All other tiers (Starter, default): 24 hours (organic only)
 *
 * @param subscriptionPlan - The business subscription plan name
 * @returns Duration in hours
 */
export const getExclusiveDurationHours = (subscriptionPlan?: string | null): number => {
  if (subscriptionPlan && subscriptionPlan.toLowerCase().includes('growth')) {
    return GROWTH_EXCLUSIVE_HOURS;
  }
  return DEFAULT_EXCLUSIVE_HOURS;
};

/**
 * Calculates the exclusive expiration time based on subscription tier.
 * - Growth tier: 48 hours from now
 * - All other tiers: 24 hours from now (default)
 *
 * @param subscriptionPlan - Optional subscription plan to determine duration
 * @returns ISO 8601 timestamp string in the future
 */
export const calculateExclusiveExpiration = (subscriptionPlan?: string | null): string => {
  const hours = getExclusiveDurationHours(subscriptionPlan);
  const now = new Date();
  const expirationTime = new Date(now.getTime() + hours * 60 * 60 * 1000);
  return expirationTime.toISOString();
};

/**
 * Checks if an exclusive period is still active
 * @param exclusiveUntil - ISO 8601 timestamp when the exclusive period expires
 * @returns true if the exclusive period is still active, false otherwise
 */
export const isExclusivePeriodActive = (exclusiveUntil: string | null | undefined): boolean => {
  if (!exclusiveUntil) return false;
  
  const now = new Date();
  const expirationDate = new Date(exclusiveUntil);
  
  return expirationDate > now;
};

/**
 * Gets the remaining time in the exclusive period
 * @param exclusiveUntil - ISO 8601 timestamp when the exclusive period expires
 * @returns Object with hours and minutes remaining, or null if expired
 */
export const getExclusiveTimeRemaining = (exclusiveUntil: string | null | undefined): { hours: number; minutes: number } | null => {
  if (!exclusiveUntil || !isExclusivePeriodActive(exclusiveUntil)) {
    return null;
  }
  
  const now = new Date();
  const expirationDate = new Date(exclusiveUntil);
  const diffMs = expirationDate.getTime() - now.getTime();
  
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  return { hours, minutes };
};

/**
 * Formats the remaining time as a human-readable string
 * @param exclusiveUntil - ISO 8601 timestamp when the exclusive period expires
 * @returns Formatted string like "23 hours 45 minutes remaining" or "Expired"
 */
export const formatExclusiveTimeRemaining = (exclusiveUntil: string | null | undefined): string => {
  const remaining = getExclusiveTimeRemaining(exclusiveUntil);
  
  if (!remaining) {
    return 'Expired';
  }
  
  if (remaining.hours === 0) {
    return `${remaining.minutes} minute${remaining.minutes !== 1 ? 's' : ''} remaining`;
  }
  
  if (remaining.minutes === 0) {
    return `${remaining.hours} hour${remaining.hours !== 1 ? 's' : ''} remaining`;
  }
  
  return `${remaining.hours} hour${remaining.hours !== 1 ? 's' : ''} ${remaining.minutes} minute${remaining.minutes !== 1 ? 's' : ''} remaining`;
};
